import { router } from "expo-router";
import { useEffect, useState } from "react";
import { Button } from "~/components/Button";
import { AlertModal } from "~/components/feedback/AlertModal";
import { TextInput } from "~/components/form/TextInput";
import { FullPageModal } from "~/components/layout/modal/FullPageModal";
import { Title } from "~/components/layout/text";
import { BodyText } from "~/components/layout/text/BodyText";
import type { ApiError } from "~/providers/ApiProvider";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useSession } from "~/providers/SessionProvider";
import { empty, filled, isEmail } from "~/util";

export default function ForgotPassword() {
    const { requestPasswordReset } = useSession();
    const { loading, setLoading } = useFeedback();
    const errorBag = useErrorBag<"email">();
    const [email, setEmail] = useState<string>("");
    const [response, setResponse] = useState<string | null>(null);

    // biome-ignore lint/correctness/useExhaustiveDependencies(errorBag.forget): error bag
    useEffect(() => {
        setEmail("");
        setResponse(null);
        errorBag.forget("email");
    }, []);

    const back = () => {
        router.dismissTo("/log-in");
    };

    const can_submit = !loading && isEmail(email) && empty(response);

    const changePassword = () => {
        if (!can_submit) {
            return;
        }
        setLoading(true);

        requestPasswordReset(email)
            .then(setResponse)
            .catch((error: ApiError) => {
                errorBag.add(
                    "email",
                    error.response?.data?.message ?? "We could not reset your password. Please try again later",
                );
            })
            .finally(() => setLoading(false));
    };

    return (
        <FullPageModal
            cancelable
            onRequestClose={back}
            title="Forgot Password"
            headerRight={() => (
                <Button disabled={!can_submit} title="Save" size="sm" onPress={changePassword} loading={loading} />
            )}
            asRoute
        >
            <Title>Forgot your password?</Title>
            <BodyText>Enter your email address and we will send you a link to create a new one.</BodyText>
            <TextInput
                name="email"
                label="Email Address"
                autoComplete="username"
                autoCapitalize={"none"}
                textContentType="emailAddress"
                value={email}
                onChangeText={setEmail}
                disabled={loading || filled(response)}
            />
            {filled(response) && <AlertModal description={response} onDismiss={back} />}
        </FullPageModal>
    );
}
